import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { NavMenu } from '../../models/navMenu.model';
import { Router, RouterModule } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { ApiService } from '../../services/api.service';
import { AuthService, UserTenant } from '../../services/auth.service';
import { NAVIGATION_PERMISSIONS } from '../../constants/groups.constants';
import { SELECTED_TENANT_NAME_STORAGE_KEY } from '../../constants/app.constants';
import { LoaderService } from '../../services/loader.service';
import { ToasterService } from '../../services/toaster.service';
import { catchError, combineLatest, firstValueFrom, of, filter, Subject, takeUntil, map, take } from 'rxjs';
import { dm_user } from '../../models/data-models/dm_user.model';
import { IconService } from '../../services/svg-icon.service';
import { ProjectService } from '../../services/project.service';
import { AddPopupComponent, PopupDialogData } from '../../ui-component-library/add-popup/add-popup.component';
import { dm_tenant } from '../../models/data-models/dm_tenant.model';
import { MobileFooterComponent } from '../mobile-footer/mobile-footer.component';
import { MobileHeaderComponent } from '../mobile-header/mobile-header.component';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css'],
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    CommonModule,
    RouterModule,
    MatDividerModule,
    MobileHeaderComponent,
    MobileFooterComponent,
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isExpanded = true; // Start expanded (full view)
  expandedMenus: Set<number> = new Set();
  appVersion: string = '';
  userName: string = '';
  isAuthenticated: boolean = false;
  private isCacheLoaded = false;
  private lastUserName: string | null = null;
  private hasProcessedAuth = false; // Prevents re-entry into auth flow
  private isLoadingAuthenticatedData = false;
  private storageEventListener: ((event: StorageEvent) => void) | null = null;
  hasMultipleTenants = false;
  private allTenants: UserTenant[] = [];
  selectedTenantName: string = '';
  private tenantSelectionCancelled = false;
  private tenantSelectedFromPopup = false;

  // Full navigation menu definition
  private fullNavMenu: NavMenu[] = [
    {
      id: 1,
      icon: 'projects',
      menutext: 'Projects',
      tooltip: 'Projects',
      route: 'projects',
      customIcon: true,
    },
    {
      id: 2,
      icon: 'experiments',
      menutext: 'Experiments',
      tooltip: 'Experiments',
      route: 'experiments',
      customIcon: true,
    },
    {
      id: 3,
      icon: 'ingredients',
      menutext: 'Ingredients',
      tooltip: 'Ingredients',
      route: 'ingredients',
      customIcon: true,
    },
    {
      id: 4,
      icon: 'products',
      menutext: 'Products',
      tooltip: 'Products',
      route: 'products',
      customIcon: true,
    },
    {
      id: 5,
      icon: 'admin_panel_settings',
      menutext: 'Admin',
      tooltip: 'Admin',
      customIcon: false,
      children: [
        {
          id: 51,
          icon: 'sell',
          menutext: 'Tags',
          tooltip: 'Tags',
          route: 'tags',
          customIcon: false,
        },
        {
          id: 52,
          icon: 'category',
          menutext: 'Categories',
          tooltip: 'Categories',
          route: 'categories',
          customIcon: false,
        },
        {
          id: 53,
          icon: 'flag_circle',
          menutext: 'Objectives',
          tooltip: 'Objectives',
          route: 'objectives',
          customIcon: false,
        },
        {
          id: 54,
          icon: 'analytics',
          menutext: 'Analytics',
          tooltip: 'Analytics',
          route: 'analytics',
          customIcon: false,
        },
      ],
    },
  ];

  // Filtered menu based on user permissions
  navMenu: NavMenu[] = [];
  private hasLoadedEntities = false;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private loaderService: LoaderService,
    private toasterService: ToasterService,
    private dialog: MatDialog,
    private router: Router,
    private iconService: IconService,
    private projectSrvc: ProjectService
  ) {}

  ngOnInit(): void {
    this.selectedTenantName = '';
    this.loaderService.show('auth-init');

    combineLatest([
      this.authService.isInitialized$.pipe(filter((init) => init === true)),
      this.authService.isAuthenticated$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(async ([, isAuth]) => {
        this.isAuthenticated = isAuth;
        if (isAuth) {
          // Prevent re-entry if auth flow already processed
          if (this.hasProcessedAuth) {
            console.log('ℹ️ Auth flow already processed, skipping...');
            return;
          }

          console.log('✅ User authenticated, checking Authorization...');

          // Get user's groups from token and auth name
          const userGroups = this.authService.getUserGroups();
          if (userGroups.length === 0) {
            console.log('No user groups found in token, clearing data');
            this.toasterService.error(`User is not allowed to use application.`, 'Close', 5000);
            this.apiService.clearAllData();
            this.navMenu = []; // Clear navigation when not authorized
            this.router.navigate(['/welcome']);
            this.loaderService.hide('auth-init');
            return;
          }
          if (this.isLoadingAuthenticatedData) {
            console.log('ℹ️ Role loading already in progress, skipping...');
            return;
          }
          this.isLoadingAuthenticatedData = true;
          const rolesLoaded = await this.loadUserRolesSync(userGroups);
          this.isLoadingAuthenticatedData = false;

          if (rolesLoaded) {
            this.hasProcessedAuth = true; // Mark as processing to prevent re-entry
            console.log('✅ User Authorised, loading data...');
            const entitiesLoaded = await this.loadEntitiesOnce();
            if (!entitiesLoaded) {
              this.hasProcessedAuth = false;
              this.apiService.clearAllData();
              this.navMenu = [];
              this.router.navigate(['/welcome']);
              this.loaderService.hide('auth-init');
              return;
            }
            this.userName = this.authService.getUserName();
            this.filterNavigationByRoles();
            // Only navigate to /projects if both Angular and the browser are on the root/welcome page.
            // During a hard refresh, router.url can still be '/' while the browser is loading a deep link.
            const currentUrl = this.router.url;
            const currentPath = window.location.pathname;
            const isRootOrWelcome = (url: string) => url === '/' || url === '/welcome' || url === '';
            if (isRootOrWelcome(currentUrl) && isRootOrWelcome(currentPath)) {
              this.router.navigate(['/projects']);
            }

            await firstValueFrom(this.apiService.entityReloadCompleted$.pipe(take(1)));

            // Show only if user explicitly selected tenant from AddPopup
            if (this.tenantSelectedFromPopup) {
              this.toasterService.success('Tenant selected. Welcome!', 'Close', 3000);
            }
            this.loaderService.hide('auth-init');
          } else if (this.tenantSelectionCancelled) {
            this.tenantSelectionCancelled = false;
            this.loaderService.hide('auth-init');
          } else {
            console.log('⚠️ User not Authorised, clearing data');
            this.apiService.clearAllData();
            this.navMenu = []; // Clear navigation when not authorized
            this.router.navigate(['/welcome']);
            this.loaderService.hide('auth-init');
          }
        } else {
          console.log('⚠️ User not authenticated, clearing data');
          this.navMenu = []; // Clear navigation when not authenticated
          this.hasProcessedAuth = false; // Reset flag on logout so next login works
          this.hasMultipleTenants = false;
          this.allTenants = [];
          this.selectedTenantName = '';
          this.tenantSelectedFromPopup = false; // reset on unauth
          this.loaderService.hide('auth-init');
        }
      });

    // Subscribe to appVersion changes
    this.apiService.appVersion$.subscribe((version) => {
      this.appVersion = version;
    });

    this.setupCrossTabLogout();
  }

  private async loadEntitiesOnce(): Promise<boolean> {
    if (this.hasLoadedEntities) return true;

    const entitiesLoaded = await this.apiService.assignEntityValues();
    this.hasLoadedEntities = entitiesLoaded;
    return entitiesLoaded;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Clean up storage event listener
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
    }
  }

  private setupCrossTabLogout(): void {
    // Listen for logout events from other tabs
    this.storageEventListener = (event: StorageEvent) => {
      if (event.key === 'logout-event' && event.newValue) {
        console.log('Logout detected in another tab, logging out this tab...');
        // Remove the logout flag to avoid triggering again
        localStorage.removeItem('logout-event');
        // Perform logout in this tab
        this.performLogout(false); // false = don't trigger storage event again
      }
    };
    window.addEventListener('storage', this.storageEventListener);
  }

  private async loadUserRolesSync(userGroups: string[]): Promise<boolean> {
    // Don't try to load if user isn't authenticated
    const account = this.authService.getActiveAccount();
    if (!account) {
      console.log('⚠️ No active account, skipping role load');
      return false;
    }

    const userAzureName = this.authService.getUserName();
    const userAzureId = this.authService.getUserId();
    const userAzureEmail = this.authService.getUserEmail();
    if (this.isCacheLoaded && this.lastUserName === userAzureName) {
      return true;
    }

    try {
      // Fetch userRoles and users via shared cache methods – prevents duplicate HTTP calls
      // if authGuard already triggered a fetch concurrently.
      const [roles, users] = await Promise.all([
        firstValueFrom(this.apiService.getUserRolesOnce().pipe(catchError(() => of([])))),
        firstValueFrom(this.apiService.getUsersOnce().pipe(catchError(() => of([])))),
      ]);

      this.apiService.userRoles = roles as any[];
      this.apiService.users = users as any[];

      if (this.apiService.users.length == 0 || this.apiService.userRoles.length == 0) {
        // User has no matching roles - deny access
        console.log(`Users/UserRoles are not retrived, API might be down. Please contact administrator.`);
        this.toasterService.error(`Something is not right. Please contact administrator.`, 'Close', 5000);
        return false;
      }

      this.isCacheLoaded = true;
      this.lastUserName = userAzureName;

      // Check if user has matching roles
      const matchedRoles = this.apiService.userRoles.filter((role) => userGroups.includes(role.userRoleObjectId));
      console.log(matchedRoles.map((r) => r.userRoleName) + ' - User role for', userAzureName);

      if (matchedRoles.length === 0) {
        // User has no matching roles - deny access
        console.log(`User '${userAzureName}' is not allowed to use application. Please contact administrator.`);
        this.toasterService.error(
          `User '${userAzureName}' is not allowed to use application. Please contact administrator.`,
          'Close',
          5000
        );
        return false;
      }
      // Check if user exists in Users table
      const isExistingUserById = this.apiService.users.find((u) => u.userObjectId === userAzureId);
      const isExistingUserByName = this.apiService.users.find((u) => u.userAuthId === userAzureName);
      if (isExistingUserByName && isExistingUserByName.userObjectId == null) {
        // Update userObjectId if missing
        console.log(`Updating userObjectId for user '${userAzureName}'`);
        await this.updateUser(userAzureName, userAzureId, userAzureEmail, isExistingUserByName);
      } else if (isExistingUserById && isExistingUserById.userAuthId != userAzureName) {
        // Update userAuthId if changed
        console.log(`Updating userAuthId for user ID '${isExistingUserById.id}' to '${userAzureName}'`);
        await this.updateUser(userAzureName, userAzureId, userAzureEmail, isExistingUserById);
      } else if (isExistingUserById && !isExistingUserById.email && userAzureEmail) {
        // Backfill email for users created before email capture was added
        console.log(`Backfilling email for user ID '${isExistingUserById.id}'`);
        await this.updateUser(userAzureName, userAzureId, userAzureEmail, isExistingUserById);
      }

      if (!isExistingUserById && !isExistingUserByName) {
        // User has permissions but doesn't exist in System - create them
        console.log('User has valid role. User not found in System, creating:', userAzureName);

        const newUser: Partial<dm_user> = {
          userAuthId: userAzureName,
          userObjectId: userAzureId,
          email: userAzureEmail,
        };

        await firstValueFrom(this.apiService.create('users', newUser));

        this.toasterService.success(`Welcome ${userAzureName}! Your account has been created.`, 'Close', 3000);
      }
      await this.loadTenants();
      const tenantEntriesByTitle = this.getTenantRoleEntriesByTitle();
      const tenantNames = [...tenantEntriesByTitle.keys()];
      this.hasMultipleTenants = tenantNames.length > 1;

      if (tenantNames.length === 0) {
        this.toasterService.error(
          `User '${userAzureName}' does not have access to any tenant. Please contact administrator.`,
          'Close',
          5000
        );
        return false;
      }

      this.tenantSelectedFromPopup = false;
      const selectedTenantName = await this.resolveSelectedTenantName(tenantNames);
      if (!selectedTenantName) {
        this.tenantSelectionCancelled = true;
        return false;
      }

      const tenantApplied = await this.applySelectedTenant(selectedTenantName, userAzureName, userAzureId);
      if (!tenantApplied) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ Failed to load user permissions:', error);
      this.toasterService.error('Failed to load user permissions. Please try again.', 'Close', 5000);
      return false;
    }
  }

  private async updateUser(
    userAzureName: string,
    userAzureId: string,
    userAzureEmail: string,
    isExistingUser: dm_user
  ) {
    const updatedUser: Partial<dm_user> = {
      userAuthId: userAzureName,
      userObjectId: userAzureId,
      email: userAzureEmail,
      id: isExistingUser.id,
      version: isExistingUser.version,
    };
    await firstValueFrom(this.apiService.update('users', updatedUser));
  }

  private async ensureTenantUserContext(
    userAzureName: string,
    userAzureId: string,
    userTenants: UserTenant[]
  ): Promise<{ tenantId: string; tenantUserId: number } | null> {
    for (const tenant of userTenants) {
      const tenantId = (tenant.tenantId || '').trim();
      const tenantName = (tenant.tenantName || tenant.tenantTitle || '').trim();

      if (!tenantId) {
        console.log(`⏭️  Skipping tenant '${tenantName}' (no tenantId)`);
        continue;
      }
      const tenantUserId = await firstValueFrom(this.authService.ensureTenantUserMapping(tenantId, userAzureId));

      if (tenantUserId != null) {
        console.log(`✅ Tenant_users mapping RESOLVED for '${tenantName}'`);
        return { tenantId, tenantUserId };
      }
      console.log(`❌ Failed to resolve Tenant_users mapping for '${tenantName}'`);
    }

    return null;
  }

  private filterNavigationByRoles(): void {
    this.navMenu = this.fullNavMenu.filter((menu) => {
      // If menu has no route, it's a parent - check children
      if (menu.children) {
        const filteredChildren = menu.children.filter((child) => child.route && this.canAccessRoute(child.route));

        // Only include parent if it has accessible children
        if (filteredChildren.length > 0) {
          menu.children = filteredChildren;
          return true;
        }
        return false;
      }
      // Single menu item - check permission
      return menu.route ? this.canAccessRoute(menu.route) : false;
    });

    this.ensureAdminExpanded();
  }

  private canAccessRoute(route: string): boolean {
    // Get allowed roles for this route
    const allowedRoles = NAVIGATION_PERMISSIONS[route];

    if (!allowedRoles) {
      // No restrictions defined, allow access
      return true;
    }

    // Check if user has any of the allowed roles by name
    const hasAccess = this.hasAnyRoleByName(allowedRoles);
    return hasAccess;
  }

  /**
   * Checks if user has a role by name (case-insensitive)
   */
  hasRoleByName(roleName: string): boolean {
    const userGroups = this.authService.getUserGroups();

    const userRoles = this.apiService.userRoles
      .filter((role) => userGroups.includes(role.userRoleObjectId))
      .map((role) => role.userRoleName.substring(0, role.userRoleName.lastIndexOf('.')));

    return userRoles.some((role) => role.toLowerCase() === roleName.toLowerCase());
  }

  /**
   * Checks if user has any of the specified role names
   */
  hasAnyRoleByName(roleNames: string[]): boolean {
    return roleNames.some((name) => this.hasRoleByName(name));
  }

  toggleSidenav() {
    this.isExpanded = !this.isExpanded;
    console.log('isExpanded:', this.isExpanded);
  }

  toggleExpansion(menuId: number) {
    if (this.expandedMenus.has(menuId)) {
      this.expandedMenus.delete(menuId);
    } else {
      this.expandedMenus.add(menuId);
    }
  }

  isMenuExpanded(menuId: number): boolean {
    return this.expandedMenus.has(menuId);
  }

  // Get the current nav width for content positioning
  get navWidth(): string {
    return this.isExpanded ? '90px' : '40px';
  }

  get marginLeft(): string {
    return this.isExpanded ? '90px' : '40px';
  }

  private ensureAdminExpanded(): void {
    if (!this.navMenu || !this.navMenu.length) return;

    const adminMenu = this.navMenu.find((m) => m.id === 4 || m.menutext.toLowerCase() === 'admin');
    if (adminMenu) {
      this.expandedMenus.add(adminMenu.id);
    }
  }

  login(): void {
    this.authService.login();
  }

  logout(): void {
    this.hasLoadedEntities = false;
    this.clearExperiment();
    this.performLogout(true);
  }

  async switchTenant(): Promise<void> {
    await this.loadTenants();

    const tenantEntriesByTitle = this.getTenantRoleEntriesByTitle();
    const tenantNames = [...tenantEntriesByTitle.keys()];

    if (tenantNames.length <= 1) {
      return;
    }

    const currentTenantName =
      sessionStorage.getItem(SELECTED_TENANT_NAME_STORAGE_KEY)?.trim() || this.selectedTenantName || undefined;
    const selectedTenant = await this.openTenantSelectionPopup(tenantNames, currentTenantName);

    if (!selectedTenant || selectedTenant === currentTenantName) {
      return;
    }

    this.loaderService.show('tenant-switch');
    try {
      const userAzureName = this.authService.getUserName();
      const userAzureId = this.authService.getUserId();
      const tenantApplied = await this.applySelectedTenant(selectedTenant, userAzureName, userAzureId);
      if (!tenantApplied) {
        console.log(`❌ Could not resolve tenant context for '${selectedTenant}'`);
        return;
      }

      if (!this.hasProcessedAuth) {
        this.hasProcessedAuth = true;
        this.tenantSelectionCancelled = false;
        this.userName = this.authService.getUserName();
        this.filterNavigationByRoles();
        const entitiesLoaded = await this.loadEntitiesOnce();
        if (!entitiesLoaded) {
          this.hasProcessedAuth = false;
          this.apiService.clearAllData();
          this.navMenu = [];
          this.router.navigate(['/welcome']);
          return;
        }
        const currentUrl = this.router.url;
        if (currentUrl === '/' || currentUrl === '/welcome' || currentUrl === '') {
          this.router.navigate(['/projects']);
        }
        await firstValueFrom(this.apiService['entityReloadCompleted$'].pipe(take(1)));
        this.toasterService.success('Tenant selected. Welcome!', 'Close', 3000);
      } else {
        const entitiesLoaded = await this.apiService.assignEntityValues(true);
        if (!entitiesLoaded) {
          this.apiService.clearAllData();
          this.navMenu = [];
          this.hasProcessedAuth = false;
          this.router.navigate(['/welcome']);
          return;
        }
        await firstValueFrom(this.apiService['entityReloadCompleted$'].pipe(take(1)));
        this.toasterService.success('Tenant switched successfully.', 'Close', 3000);
      }
    } finally {
      this.loaderService.hide('tenant-switch');
    }
  }

  get showSwitchTenant(): boolean {
    return this.isAuthenticated && this.hasMultipleTenants && (this.hasProcessedAuth || this.tenantSelectionCancelled);
  }

  get isWelcomeRoute(): boolean {
    const path = (this.router.url || '').split('?')[0].split('#')[0];
    return path === '/welcome' || path === '/';
  }

  get isProjectsRoute(): boolean {
    const path = (this.router.url || '').split('?')[0].split('#')[0];
    return path === '/projects';
  }

  get showMobileChrome(): boolean {
    return this.isAuthenticated && !this.isWelcomeRoute;
  }

  private async openTenantSelectionPopup(tenantNames: string[], currentTenantName?: string): Promise<string | null> {
    const dialogRef = this.dialog.open(AddPopupComponent, {
      data: <PopupDialogData>{
        title: 'Select Tenant',
        confirmText: 'Continue',
        cancelText: 'Cancel',
        radioList: tenantNames,
        defaultRadioSelected: currentTenantName || '',
        disableConfirmWhenDefaultRadioSelected: !!currentTenantName,
        id_icon: '',
      },
      disableClose: true,
      autoFocus: false,
      width: 'min(400px, calc(100vw - 24px))',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: '90vh',
      panelClass: 'tenant-selection-dialog',
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    return result?.radioSelected ?? null;
  }

  private async loadTenants(forceRefresh: boolean = false): Promise<UserTenant[]> {
    if (!forceRefresh && this.allTenants.length > 0) {
      return this.allTenants;
    }

    const activeTenantId = sessionStorage.getItem('tenantId');
    const activeTenantUserId = sessionStorage.getItem('tenantUserId');

    try {
      // Temporarily clear tenant context so GET tenants returns the complete DB tenant list,
      // not only the currently selected tenant after a page refresh.
      this.authService.clearTenantId();
      this.authService.clearTenantUserId();

      const rows = await firstValueFrom(
        this.apiService.get('tenants').pipe(
          map((res) => res.body as dm_tenant[]),
          catchError(() => of([] as dm_tenant[]))
        )
      );

      this.allTenants = (rows || [])
        .map((row) => {
          const tenantTitle = String(row?.tenantTitle ?? '').trim();
          const tenantId = String(row?.tenantId ?? '').trim();
          return {
            tenantName: tenantTitle,
            tenantTitle,
            tenantId: tenantId.length > 0 ? tenantId : null,
          } as UserTenant;
        })
        .filter((tenant) => tenant.tenantName.length > 0);

      return this.allTenants;
    } finally {
      if (activeTenantId) {
        this.authService.setTenantId(activeTenantId);
      }
      if (activeTenantUserId) {
        const parsedTenantUserId = Number(activeTenantUserId);
        this.authService.setTenantUserId(Number.isInteger(parsedTenantUserId) ? parsedTenantUserId : null);
      }
    }
  }

  private async resolveSelectedTenantName(tenantNames: string[]): Promise<string | null> {
    const storedTenant = sessionStorage.getItem(SELECTED_TENANT_NAME_STORAGE_KEY)?.trim() ?? '';
    if (storedTenant && tenantNames.includes(storedTenant)) {
      this.tenantSelectedFromPopup = false;
      return storedTenant;
    }

    if (storedTenant && !tenantNames.includes(storedTenant)) {
      sessionStorage.removeItem(SELECTED_TENANT_NAME_STORAGE_KEY);
    }

    if (tenantNames.length === 1) {
      this.tenantSelectedFromPopup = false;
      return tenantNames[0];
    }

    this.selectedTenantName = '';
    this.loaderService.hide('auth-init');
    const selected = await this.openTenantSelectionPopup(tenantNames);
    this.tenantSelectedFromPopup = !!selected; // true only when popup selection made
    if (selected) {
      this.loaderService.show('auth-init');
    }
    return selected;
  }

  private findTenantByName(tenantName: string): UserTenant | undefined {
    const selectedTenantName = tenantName.trim().toLowerCase();
    return this.allTenants.find(
      (tenant) =>
        String(tenant.tenantName || tenant.tenantTitle || '')
          .trim()
          .toLowerCase() === selectedTenantName
    );
  }

  private async applySelectedTenant(
    selectedTenantName: string,
    userAzureName: string,
    userAzureId: string
  ): Promise<boolean> {
    const selectedTenant = await this.ensureSelectedTenantExists(selectedTenantName);
    if (!selectedTenant) {
      return false;
    }

    const tenantContext = await this.ensureTenantUserContext(userAzureName, userAzureId, [selectedTenant]);

    if (!tenantContext) {
      this.toasterService.error(
        `User '${userAzureName}' is not configured for the assigned tenant. Please contact administrator.`,
        'Close',
        5000
      );
      return false;
    }

    this.authService.setTenantUserId(tenantContext.tenantUserId);
    this.authService.setTenantId(tenantContext.tenantId);
    this.selectedTenantName = (selectedTenant.tenantName || selectedTenant.tenantTitle || '').trim();
    sessionStorage.setItem(SELECTED_TENANT_NAME_STORAGE_KEY, this.selectedTenantName);
    return true;
  }

  /**
   * Creates only the selected tenant, and only if it does not already exist in DB.
   *
   * Flow:
   * 1. If selected tenant exists in allTenants, return it without calling POST tenants API.
   * 2. If selected tenant does not exist in allTenants, POST selected tenant only.
   * 3. Refresh allTenants after creating the tenant.
   * 4. Return the refreshed selected tenant from allTenants.
   */
  private async ensureSelectedTenantExists(selectedTenantName: string): Promise<UserTenant | null> {
    const tenantName = selectedTenantName.trim();
    if (!tenantName) {
      return null;
    }

    let tenant = this.findTenantByName(tenantName);
    if (tenant) {
      return tenant;
    }

    const roleEntry = this.getTenantRoleEntryByName(tenantName);
    await firstValueFrom(
      this.apiService.create('tenants', {
        tenantTitle: tenantName,
        tenantId: roleEntry?.tenantIdFromRole ?? null,
      })
    );
    console.log(`✅ Tenant '${tenantName}' created successfully.`);

    await this.loadTenants(true);
    tenant = this.findTenantByName(tenantName);
    return tenant ?? null;
  }

  private getTenantRoleEntryByName(tenantName: string): { tenantTitle: string; tenantIdFromRole: string } | null {
    const tenantEntriesByTitle = this.getTenantRoleEntriesByTitle();
    return tenantEntriesByTitle.get(tenantName.trim()) ?? null;
  }
  private getTenantRoleEntriesByTitle(): Map<string, { tenantTitle: string; tenantIdFromRole: string }> {
    const groupPrefix = 'Chef.Category.';
    const tokenGroups = this.authService.getUserGroups();
    const tenantEntriesByTitle = new Map<string, { tenantTitle: string; tenantIdFromRole: string }>();
    this.apiService.userRoles
      .filter((role) => tokenGroups.includes(role.userRoleObjectId))
      .map((role) => ({
        tenantTitle: String(role.userRoleName || '').trim(),
        tenantIdFromRole: String((role as any).userObjectId ?? role.userRoleObjectId ?? '').trim(),
      }))
      .filter((role) => role.tenantTitle.startsWith(groupPrefix))
      .map((role) => ({
        tenantTitle: role.tenantTitle.substring(groupPrefix.length).trim(),
        tenantIdFromRole: role.tenantIdFromRole,
      }))
      .filter((role) => role.tenantTitle.length > 0)
      .forEach((entry) => {
        if (!tenantEntriesByTitle.has(entry.tenantTitle)) {
          tenantEntriesByTitle.set(entry.tenantTitle, entry);
        }
      });
    return tenantEntriesByTitle;
  }

  private async performLogout(notifyOtherTabs: boolean = true): Promise<void> {
    // Show loader during logout process
    this.loaderService.show('logout');

    // Only the initiating tab records the audit event — passive tabs reacting to the
    // cross-tab 'logout-event' storage signal would otherwise each log a duplicate event.
    if (notifyOtherTabs) {
      await firstValueFrom(this.apiService.recordAuthEvent('logout'));
    }

    // Notify other tabs about logout if this is the initiating tab
    if (notifyOtherTabs) {
      localStorage.setItem('logout-event', Date.now().toString());
      // Clean up immediately after setting to avoid issues
      setTimeout(() => localStorage.removeItem('logout-event'), 100);
    }

    // Clear local state immediately for better UX
    this.navMenu = [];
    this.userName = '';
    this.isAuthenticated = false;
    this.hasProcessedAuth = false; // Reset so next login works
    this.isCacheLoaded = false; // Reset cache flag
    this.lastUserName = null;
    this.hasMultipleTenants = false;
    this.allTenants = [];
    this.selectedTenantName = '';
    this.tenantSelectedFromPopup = false;
    sessionStorage.removeItem(SELECTED_TENANT_NAME_STORAGE_KEY);
    this.authService.clearTenantUserId();
    this.authService.clearTenantId();

    // Logout will redirect to login page automatically
    // Loader will be cleared when page redirects
    this.clearSessionStorage();
    this.authService.logout();
  }

  clearSessionStorage() {
    sessionStorage.removeItem('showCostData');
  }

  onNavClick() {
    this.clearExperiment();
  }

  clearExperiment() {
    this.projectSrvc.clearExperimentForEdit();
  }
}
